'use client'

import { classMerge } from '@components/utilities/classMerge'
import checkIfSafariBrowser from '@lib/utilities/checkIfSafariBrowser'
import { useEffect, useState } from 'react'
import { type EllipseConfig } from './consts'

// Helper function to create animation values string
const createAnimationValues = (points: { x: number; y: number }[]) => {
  return points.map((p) => `${p.x},${p.y}`).join('; ')
}

// Component to render a single ellipse with optional animations
const AnimatedEllipse = ({
  config,
  shouldAnimate,
}: {
  config: EllipseConfig
  shouldAnimate: boolean
}) => {
  const { animation, cx, cy, fill, rx, ry, transform } = config

  if (!animation || !shouldAnimate) {
    return (
      <ellipse
        cx={cx}
        cy={cy}
        fill={fill}
        rx={rx}
        ry={ry}
        transform={transform}
      />
    )
  }

  return (
    <ellipse cx={cx} cy={cy} fill={fill} rx={rx} ry={ry} transform={transform}>
      <animateTransform
        additive='sum'
        attributeName='transform'
        dur={animation.duration}
        repeatCount='indefinite'
        type='translate'
        values={createAnimationValues(animation.translatePath)}
      />
      <animate
        attributeName='rx'
        dur={animation.duration}
        repeatCount='indefinite'
        values={animation.scaleRx.join('; ')}
      />
      <animate
        attributeName='ry'
        dur={animation.duration}
        repeatCount='indefinite'
        values={animation.scaleRy.join('; ')}
      />
      {animation.opacity && (
        <animate
          attributeName='opacity'
          dur={animation.duration}
          repeatCount='indefinite'
          values={animation.opacity.join('; ')}
        />
      )}
      {animation.fill && (
        <animate
          attributeName='fill'
          dur={animation.duration}
          repeatCount='indefinite'
          values={animation.fill.join('; ')}
        />
      )}
    </ellipse>
  )
}

// Main component
export const AnimatedSvgGradient = ({
  className,
  ellipses,
}: {
  className?: string
  ellipses: EllipseConfig[]
}) => {
  const [shouldAnimate, setShouldAnimate] = useState(false)
  const [hydrated, setHydrated] = useState(false)

  useEffect(() => {
    const isSafari = checkIfSafariBrowser()
    if (!isSafari) {
      // only animate on non-safari browsers: safari memory hogs when animating svgs
      setShouldAnimate(true)
    }
    setHydrated(true)
  }, [])

  return (
    <svg
      className={classMerge(
        className,
        'transition-opacity duration-[3000ms]',
        hydrated ? 'opacity-70' : 'opacity-0',
      )}
      fill='none'
      preserveAspectRatio='xMidYMid slice'
      viewBox='0 0 1512 670'
      xmlns='http://www.w3.org/2000/svg'
    >
      <g filter='url(#filter0_f_171_1793)'>
        {ellipses.map((config) => (
          <AnimatedEllipse
            config={config}
            key={config.id}
            shouldAnimate={shouldAnimate}
          />
        ))}
      </g>
      <defs>
        <filter
          colorInterpolationFilters='sRGB'
          filterUnits='userSpaceOnUse'
          height='1036'
          id='filter0_f_171_1793'
          width='2072'
          x='-280'
          y='-90'
        >
          <feFlood floodOpacity='0' result='BackgroundImageFix' />
          <feBlend
            in='SourceGraphic'
            in2='BackgroundImageFix'
            mode='normal'
            result='shape'
          />
          {/* Kathleen's gradient in figma had a 150 gaussian blur, but safari doesn't like blurs above 65. this looks close enough, and then I cranked down the opacity to 0.7 to make it look closer to the figma version */}
          <feGaussianBlur stdDeviation={shouldAnimate ? 100 : 60} />
        </filter>
      </defs>
    </svg>
  )
}
